/**
 * Theme Generator
 *
 * Uses chroma-js to generate complete theme color scales from a single base color.
 * Creates all the CSS variable overrides needed for a custom accent theme.
 */
import chroma from 'chroma-js';

import { ThemePreset } from './custom-theme-presets';

/**
 * Generate a color scale from a base color
 * Returns an object with keys 100-800 representing lightest to darkest
 */
export const generateColorScale = (baseColor: string): Record<string, string> => {
	const base = chroma(baseColor);

	// Generate a scale from light to dark
	// We use LAB color space for perceptually uniform gradients
	const lightness = base.get('lab.l');

	// Create lighter and darker variants
	const scale: Record<string, string> = {};

	// Lighter shades (100-300)
	scale['100'] = base.set('lab.l', Math.min(95, lightness + 35)).hex();
	scale['200'] = base.set('lab.l', Math.min(90, lightness + 25)).hex();
	scale['300'] = base.set('lab.l', Math.min(80, lightness + 15)).hex();

	// Mid shades (400-500) - base color lives around here
	scale['400'] = base.set('lab.l', Math.min(70, lightness + 5)).hex();
	scale['500'] = base.hex(); // The chosen color
	scale['600'] = base.set('lab.l', Math.max(30, lightness - 10)).hex();

	// Darker shades (700-800)
	scale['700'] = base.set('lab.l', Math.max(20, lightness - 20)).hex();
	scale['800'] = base.set('lab.l', Math.max(15, lightness - 30)).hex();

	return scale;
};

/**
 * Generate alpha (transparent) variants of a color
 */
export const generateAlphaScale = (baseColor: string): Record<string, string> => {
	const base = chroma(baseColor);

	return {
		'100': base.alpha(0.08).css(),
		'200': base.alpha(0.16).css(),
		'300': base.alpha(0.24).css(),
	};
};

/**
 * Determine if a color is "light" (needs dark text) or "dark" (needs light text)
 */
export const isLightColor = (color: string): boolean => {
	return chroma(color).luminance() > 0.4;
};

/**
 * Get a contrasting text color for a given background
 */
export const getContrastingText = (bgColor: string): string => {
	return isLightColor(bgColor) ? '#1a1a1a' : '#ffffff';
};

/**
 * User-generated theme stored in localStorage
 */
export interface UserGeneratedTheme {
	id: string;
	name: string;
	baseColor: string;
	createdAt: string;
}

const USER_THEMES_STORAGE_KEY = 'bonusly-user-themes';

/**
 * Get all user-generated themes from localStorage
 */
export const getUserThemes = (): UserGeneratedTheme[] => {
	if (typeof window === 'undefined') return [];
	try {
		const stored = localStorage.getItem(USER_THEMES_STORAGE_KEY);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
};

/**
 * Save a user-generated theme to localStorage
 */
export const saveUserTheme = (theme: UserGeneratedTheme): void => {
	const themes = getUserThemes();
	const existingIndex = themes.findIndex((t) => t.id === theme.id);

	if (existingIndex >= 0) {
		themes[existingIndex] = theme;
	} else {
		themes.push(theme);
	}

	localStorage.setItem(USER_THEMES_STORAGE_KEY, JSON.stringify(themes));
};

/**
 * Delete a user-generated theme from localStorage
 */
export const deleteUserTheme = (themeId: string): void => {
	const themes = getUserThemes().filter((t) => t.id !== themeId);
	localStorage.setItem(USER_THEMES_STORAGE_KEY, JSON.stringify(themes));
};

/**
 * Generate a complete theme preset from a single base color
 */
export const generateThemeFromColor = (baseColor: string, name?: string): ThemePreset => {
	const scale = generateColorScale(baseColor);
	const alphaScale = generateAlphaScale(baseColor);

	return {
		id: `user-${baseColor.replace('#', '')}`,
		name: name ?? 'Custom',
		category: 'accent',
		description: `Custom theme based on ${baseColor}`,
		preview: {
			primary: scale['500'],
			secondary: scale['600'],
			accent: scale['400'],
		},
		overrides: {
			// Primary colors
			'--chakra-colors-primary-fg': scale['500'],
			'--chakra-colors-primary-fgStrong': scale['600'],
			'--chakra-colors-primary-fgStronger': scale['700'],
			'--chakra-colors-primary-fgStrongest': scale['800'],
			'--chakra-colors-primary-bgAlpha': alphaScale['100'],
			'--chakra-colors-primary-bgAlphaHover': alphaScale['200'],
			'--chakra-colors-primary-bgAlphaActive': alphaScale['300'],
			'--chakra-colors-primary-bg': scale['500'],
			'--chakra-colors-primary-bgHover': scale['600'],
			'--chakra-colors-primary-bgActive': scale['700'],
			// Interactive text
			'--chakra-colors-text-interactive': scale['500'],
			// Border focus
			'--chakra-colors-border-focus': scale['500'],
		},
	};
};

/**
 * Generate a full theme (including backgrounds) from a base color
 * This creates a more immersive dark theme experience
 */
export const generateFullThemeFromColor = (baseColor: string, name?: string): ThemePreset => {
	const base = chroma(baseColor);
	const scale = generateColorScale(baseColor);
	const alphaScale = generateAlphaScale(baseColor);

	// Generate dark background colors based on the hue of the base color
	const hue = base.get('hsl.h');
	const sat = Math.min(base.get('hsl.s'), 0.3); // Desaturate for backgrounds

	// Create dark backgrounds with a tint of the base color
	const bgSurface = chroma.hsl(hue, sat * 0.5, 0.08).hex();
	const bgCanvas = chroma.hsl(hue, sat * 0.4, 0.12).hex();
	const bgInset = chroma.hsl(hue, sat * 0.4, 0.1).hex();
	const bgSoft = chroma.hsl(hue, sat * 0.3, 0.16).hex();
	const bgSubtle = chroma.hsl(hue, sat * 0.5, 0.04).hex();

	// Generate complementary accent for hashtags/points
	const accentHue = (hue + 45) % 360; // Analogous color
	const accent = chroma.hsl(accentHue, 0.7, 0.6).hex();

	// Light text colors with a hint of the base color
	const textDefault = chroma.hsl(hue, sat * 0.1, 0.92).hex();
	const textSoft = chroma.hsl(hue, sat * 0.15, 0.75).hex();
	const textSofter = chroma.hsl(hue, sat * 0.2, 0.6).hex();
	const textSoftest = chroma.hsl(hue, sat * 0.2, 0.45).hex();

	// Border colors
	const borderAlpha = chroma(textSoft).alpha(0.25).css();
	const borderSoftAlpha = chroma(textSoft).alpha(0.15).css();
	const borderStrongAlpha = chroma(textSoft).alpha(0.4).css();

	return {
		id: `user-full-${baseColor.replace('#', '')}`,
		name: name ?? 'Custom Full',
		category: 'full',
		description: `Full custom theme based on ${baseColor}`,
		preview: {
			primary: bgSurface,
			secondary: bgSoft,
			accent: scale['400'],
		},
		overrides: {
			// Backgrounds
			'--chakra-colors-bg-canvas': bgCanvas,
			'--chakra-colors-bg-surface': bgSurface,
			'--chakra-colors-bg-inset': bgInset,
			'--chakra-colors-bg-soft': bgSoft,
			'--chakra-colors-bg-subtle': bgSubtle,
			'--chakra-colors-bg-topNav': bgSurface,

			// Text
			'--chakra-colors-text-default': textDefault,
			'--chakra-colors-text-soft': textSoft,
			'--chakra-colors-text-softer': textSofter,
			'--chakra-colors-text-softest': textSoftest,
			'--chakra-colors-text-onColor': getContrastingText(scale['500']),

			// Borders
			'--chakra-colors-border-default': borderAlpha,
			'--chakra-colors-border-soft': borderSoftAlpha,
			'--chakra-colors-border-strong': borderStrongAlpha,

			// Primary colors
			'--chakra-colors-primary-fg': scale['400'],
			'--chakra-colors-primary-fgStrong': scale['300'],
			'--chakra-colors-primary-bg': scale['500'],
			'--chakra-colors-primary-bgHover': scale['400'],
			'--chakra-colors-primary-bgActive': scale['600'],
			'--chakra-colors-primary-bgAlpha': alphaScale['200'],
			'--chakra-colors-primary-bgAlphaHover': alphaScale['300'],

			// Secondary
			'--chakra-colors-secondary-bg': textDefault,
			'--chakra-colors-secondary-bgHover': chroma(textDefault).darken(0.3).hex(),
			'--chakra-colors-secondary-fg': textDefault,
			'--chakra-colors-secondary-fgOnSurface': bgSurface,
			'--chakra-colors-secondary-bgAlpha': chroma(textDefault).alpha(0.15).css(),
			'--chakra-colors-secondary-bgAlphaHover': chroma(textDefault).alpha(0.25).css(),

			// Points
			'--chakra-colors-points-fg': accent,
			'--chakra-colors-points-fgHover': chroma(accent).brighten(0.5).hex(),
			'--chakra-colors-points-fgOnSurface': accent,
			'--chakra-colors-points-bgAlpha': chroma(accent).alpha(0.2).css(),

			// Hashtags
			'--chakra-colors-hashtag-fg': scale['300'],
			'--chakra-colors-hashtag-fgOnSurface': scale['300'],
			'--chakra-colors-text-hashtag': scale['300'],

			// Interactive
			'--chakra-colors-text-interactive': scale['300'],
			'--chakra-colors-border-focus': scale['400'],
		},
	};
};

/**
 * Validate that a string is a valid hex color
 */
export const isValidHexColor = (color: string): boolean => {
	try {
		chroma(color);
		return true;
	} catch {
		return false;
	}
};
